import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, DatePicker, InputNumber, Table, App, Typography, Input, Select } from 'antd';
import dayjs from 'dayjs';
import {
  purchaseOrderService,
  type PurchaseOrderDTO,
  type PurchaseOrderItemDTO,
  type CreateEnvelopeChildPayload,
} from '@/services/purchaseOrder.service';
import { envelopeChildActionLabel, normalizePoType } from '@/constants/purchaseOrderTypes';
import { useProjectsForSelection } from '@/hooks/useProject';

const { Text } = Typography;

export interface CreateEnvelopeChildModalProps {
  open: boolean;
  parentPo: PurchaseOrderDTO;
  parentItems: PurchaseOrderItemDTO[];
  onClose: () => void;
  onCreated: (child: PurchaseOrderDTO) => void;
}

type LineDraft = {
  parent_po_item_id: string;
  item_description: string;
  ordered_remaining: number;
  unit_price: number;
  uom?: string;
  qty: number;
  project_id?: string | null;
};

/**
 * Create a release (Blanket/Planned) or call-off (Contract) against an envelope parent PO.
 */
export const CreateEnvelopeChildModal: React.FC<CreateEnvelopeChildModalProps> = ({
  open,
  parentPo,
  parentItems,
  onClose,
  onCreated,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const { data: projects = [] } = useProjectsForSelection();

  const action = envelopeChildActionLabel(parentPo.po_type);
  const actionTitle = action === 'call-off' ? 'Create call-off' : 'Create release';

  const projectOptions = useMemo(
    () =>
      (projects as Array<{ id: string; project_number?: string; project_name?: string }>).map((p) => ({
        value: p.id,
        label: [p.project_number, p.project_name].filter(Boolean).join(' — ') || p.id,
      })),
    [projects]
  );

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      delivery_date: dayjs().add(7, 'day'),
      notes: '',
      project_id: parentPo.project_id || undefined,
    });
    setLines(
      parentItems.map((it) => {
        const ordered = Number(it.ordered_quantity) || 0;
        const released = Number(it.released_quantity) || 0;
        const remaining = Math.max(0, ordered - released);
        return {
          parent_po_item_id: it.id,
          item_description: it.item_description,
          ordered_remaining: remaining,
          unit_price: Number(it.unit_price) || 0,
          uom: it.uom,
          qty: remaining > 0 ? remaining : 0,
          project_id: it.project_id || parentPo.project_id || null,
        };
      })
    );
  }, [open, parentItems, form, parentPo.project_id]);

  const selectedLines = useMemo(() => lines.filter((l) => l.qty > 0), [lines]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedLines.length) {
        message.error('Enter a quantity greater than zero on at least one line');
        return;
      }
      for (const l of selectedLines) {
        if (l.qty - l.ordered_remaining > 0.0001) {
          message.error(`Quantity for "${l.item_description}" exceeds remaining ${l.ordered_remaining}`);
          return;
        }
      }
      const headerProject =
        values.project_id != null && String(values.project_id).trim()
          ? String(values.project_id).trim()
          : null;
      const payload: CreateEnvelopeChildPayload = {
        delivery_date: values.delivery_date.format('YYYY-MM-DD'),
        notes: values.notes || undefined,
        project_id: headerProject,
        lines: selectedLines.map((l) => ({
          parent_po_item_id: l.parent_po_item_id,
          ordered_quantity: l.qty,
          project_id: l.project_id || headerProject,
        })),
      };
      setSubmitting(true);
      const child =
        action === 'call-off'
          ? await purchaseOrderService.createCallOff(parentPo.id, payload)
          : await purchaseOrderService.createRelease(parentPo.id, payload);
      message.success(`${normalizePoType(parentPo.po_type)} ${action} ${child.po_number} created`);
      onCreated(child);
      onClose();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || `Failed to create ${action}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`${actionTitle} — ${parentPo.po_number}`}
      open={open}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={submitting}
      okText={actionTitle}
      width={920}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Prices are locked from the parent. Delivery date is required. Remaining qty excludes already-released
        amounts (open draft {action}s also reserve remaining on the server).
      </Text>
      <Form form={form} layout="vertical">
        <Form.Item
          name="delivery_date"
          label="Delivery date"
          rules={[{ required: true, message: 'Delivery date is required' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="project_id"
          label="Project override (optional)"
          extra="Applied to lines that do not set their own project"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Keep parent / line projects"
            options={projectOptions}
          />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
      <Table
        size="small"
        rowKey="parent_po_item_id"
        pagination={false}
        dataSource={lines}
        scroll={{ x: 800 }}
        columns={[
          {
            title: 'Description',
            dataIndex: 'item_description',
            ellipsis: true,
          },
          {
            title: 'Remaining',
            dataIndex: 'ordered_remaining',
            width: 100,
            align: 'right',
            render: (v: number, row) => `${v}${row.uom ? ` ${row.uom}` : ''}`,
          },
          {
            title: 'Unit price',
            dataIndex: 'unit_price',
            width: 110,
            align: 'right',
            render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }),
          },
          {
            title: 'Project',
            dataIndex: 'project_id',
            width: 180,
            render: (_: string | null | undefined, row, index) => (
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
                style={{ width: '100%' }}
                placeholder="—"
                value={row.project_id || undefined}
                options={projectOptions}
                onChange={(v) => {
                  setLines((prev) => {
                    const next = [...prev];
                    const cur = next[index];
                    if (!cur) return prev;
                    next[index] = { ...cur, project_id: v || null };
                    return next;
                  });
                }}
              />
            ),
          },
          {
            title: 'Release qty',
            dataIndex: 'qty',
            width: 130,
            render: (_: number, row, index) => (
              <InputNumber
                min={0}
                max={row.ordered_remaining}
                step={1}
                value={row.qty}
                style={{ width: '100%' }}
                disabled={row.ordered_remaining <= 0}
                onChange={(v) => {
                  setLines((prev) => {
                    const next = [...prev];
                    const cur = next[index];
                    if (!cur) return prev;
                    next[index] = { ...cur, qty: Number(v) || 0 };
                    return next;
                  });
                }}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default CreateEnvelopeChildModal;
